import {DateTime} from 'luxon';
import {transparentize} from 'polished';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';
import {DefaultTheme} from 'styled-components/dist/types';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  AnalyticsJobStatusEnum,
  AnalyticsMetricTypesEnum,
} from '../../../../../../libs/shared/core/src/models/api/cellsAnalytics';
import {parseTeamNamespace} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InlineBanner,
  InlineBannerLayoutTypesEnum,
  InlineBannerTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  getCellsAnalyticsReportUpdateDate,
  shouldDismissCellsAnalyticsInfoBanner,
} from '../../../../../analytics/helpers/cellsAnalyticsReportHelpers';
import {renderDurationValue} from '../../../../../analytics/helpers/metricRenderers/durationMetricRenderer';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {DismissedAnalyticsInfoBanner} from '../../../../../models/api/localPreferencesModel';
import {navigateToAnalyticsFromCellsAnalyticsReportAction} from '../../../../../store/data/analytics/cellsAnalyticsBannerActions';
import {makeGetInbox} from '../../../../../store/data/inboxes/inboxesSelectors';
import {
  dismissAnalyticsInfoBannerOnViewOnAnalytics,
  updateDismissedAnalyticsInfoBanner,
  updateDismissedAnalyticsInfoBannerSeenAt,
} from '../../../../../store/data/localPreferences/localPreferencesActions';
import {getDismissedAnalyticsInfoBanner} from '../../../../../store/data/localPreferences/localPreferencesSelectors';
import {InboxesInboxParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';

/*
 * Interface.
 */

interface AnalyticsInfoBannerProps {
  parameters: InboxesInboxParameters;
}

/*
 * Style.
 */

const topBarColor = (theme: DefaultTheme) => theme.palette.blue.shade40;
const topBarTextColor = (theme: DefaultTheme) => theme.palette.blue.shade50;
const bottomBarColor = (theme: DefaultTheme) => theme.greys.shade60;
const bottomBarTextColor = (theme: DefaultTheme) => theme.greys.shade70;

const StyledInfoBanner = styled(InlineBanner)`
  grid-row: 1;
  grid-column: 1;
  margin-top: 16px;
  margin-right: 7px;
  margin-left: 14px;
  margin-bottom: 8px;
`;

const StyledBarsContainerDiv = styled.div`
  margin-top: 8px;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledBarDiv = styled.div<{$getColorFromPalette: (theme: DefaultTheme) => string}>`
  align-self: center;
  border-radius: 4px;
  height: 8px;
  background: linear-gradient(
    90deg,
    ${(p) => transparentize(0.8, p.$getColorFromPalette(p.theme))} 0%,
    ${(p) => p.$getColorFromPalette(p.theme)} 100%
  );
  width: 100%;
  min-width: 8px;
`;

const StyledBarWrapperDiv = styled.div<{$width: string}>`
  display: flex;
  gap: 4px;
  width: ${(p) => p.$width};
  height: 12px;
`;

const StyledDurationDiv = styled.div<{$getColorFromPalette: (theme: DefaultTheme) => string}>`
  color: ${(p) => p.$getColorFromPalette(p.theme)};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.medium};
  text-transform: uppercase;
  align-self: center;
  white-space: nowrap;
  width: 40px;
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
`;

/*
 * Component.
 */

export const AnalyticsInfoBanner: FC<AnalyticsInfoBannerProps> = ({parameters}) => {
  const dispatch = useAppDispatch();
  const analyticsInfoBannerDismissal = useAppSelector(getDismissedAnalyticsInfoBanner);
  const maybeInfoBannerContext = useInfoBannerContext(parameters, analyticsInfoBannerDismissal);
  const [shouldShowAgainDialog, setShouldShowAgainDialog] = useState(false);
  const {trackDataEvent} = useDataEventTracking();

  useInitialEffect(() => {
    if (maybeInfoBannerContext?.inboxId) {
      trackDataEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.ANALYTICS_BANNER, {
        metric_type: maybeInfoBannerContext?.metricId,
        metric_variation: metricVariation,
        inbox_id: maybeInfoBannerContext?.inboxId,
      });
      dispatch(updateDismissedAnalyticsInfoBannerSeenAt());
    }
  });

  if (!maybeInfoBannerContext) {
    return null;
  }

  const {metric, metricId, inboxId, teamId, metricVariation} = maybeInfoBannerContext;

  const closeBannerAndTrack = (shouldNeverShowAgain: boolean) => {
    const lastsWeeksReportDate = getCellsAnalyticsReportUpdateDate(DateTime.now().minus({weeks: 1}));
    const isInARow = Boolean(
      analyticsInfoBannerDismissal?.lastDismissedDate &&
        analyticsInfoBannerDismissal.lastDismissedDate > lastsWeeksReportDate.toMillis(),
    );
    const weeksInARow = isInARow ? analyticsInfoBannerDismissal?.weeksInARow ?? 0 : 0;
    const dismissClicks = analyticsInfoBannerDismissal?.dismissClicks ?? weeksInARow;

    trackDataEvent(DataEventActionsEnum.CLOSE, AnalyticsDataEventObject.ANALYTICS_BANNER, {
      metric_type: metricId,
      metric_variation: metricVariation,
      inbox_id: inboxId,
      consecutive_weeks: weeksInARow + 1,
      dismiss_clicks: dismissClicks + 1,
      never_show_again: shouldNeverShowAgain,
    });
    dispatch(updateDismissedAnalyticsInfoBanner(shouldNeverShowAgain, isInARow));
    setShouldShowAgainDialog(false);
  };

  const onBannerClose = () => {
    // we use weeksInARow as a fallback for the first week of introducing dismissClicks
    const dismissClicks =
      analyticsInfoBannerDismissal?.dismissClicks ?? analyticsInfoBannerDismissal?.weeksInARow ?? 0;
    // if it's the 3rd week in a row, show the popup
    if (analyticsInfoBannerDismissal && dismissClicks % 3 === 2) {
      setShouldShowAgainDialog(true);
      return;
    }

    closeBannerAndTrack(false);
  };

  const goToAnalytics = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, AnalyticsDataEventObject.ANALYTICS_BANNER, {
      metric_type: metricId,
      metric_variation: metricVariation,
      dismiss_clicks: analyticsInfoBannerDismissal?.dismissClicks ?? 0,
      inbox_id: inboxId,
    });
    dispatch(dismissAnalyticsInfoBannerOnViewOnAnalytics());
    dispatch(navigateToAnalyticsFromCellsAnalyticsReportAction(inboxId, teamId));
  };

  const renderShowAgainDialog = () => (
    <OkCancelDialog
      title={<Trans id="analytics-info-banner-show-again-title">Show again next week?</Trans>}
      okButtonLabel={<Trans id="yes">Yes</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      cancelButtonLabel={<Trans id="analytics-info-banner-never-show-again-button">Never show again</Trans>}
      cancelButtonVariant={ButtonVariantsEnum.SECONDARY_DANGER}
      onOkClick={() => closeBannerAndTrack(false)}
      onRequestClose={() => closeBannerAndTrack(true)}
    >
      <Trans id="analytics-info-banner-show-again-description">
        Continue receiving insights like this once per week to get a pulse of your reply time.
      </Trans>
    </OkCancelDialog>
  );

  const renderBars = () => {
    const {value, previousValue} = metric;
    const lastWeek = renderDurationValue({value});
    const weekPrior = previousValue ? renderDurationValue({value: previousValue}) : null;

    const lastWeekWidth = value >= previousValue ? '100%' : `${Math.round((value / previousValue) * 100)}%`;
    const weekPriorWidth = previousValue >= value ? '100%' : `${Math.round((previousValue / value) * 100)}%`;

    return (
      <StyledBarsContainerDiv>
        <StyledBarWrapperDiv $width={lastWeekWidth}>
          <StyledBarDiv $getColorFromPalette={topBarColor} />
          <StyledDurationDiv $getColorFromPalette={topBarTextColor}>{lastWeek}</StyledDurationDiv>
        </StyledBarWrapperDiv>

        {weekPrior && (
          <StyledBarWrapperDiv $width={weekPriorWidth}>
            <StyledBarDiv $getColorFromPalette={bottomBarColor} />
            <StyledDurationDiv $getColorFromPalette={bottomBarTextColor}>{weekPrior}</StyledDurationDiv>
          </StyledBarWrapperDiv>
        )}
        {shouldShowAgainDialog && renderShowAgainDialog()}
      </StyledBarsContainerDiv>
    );
  };

  return (
    <StyledInfoBanner
      layoutType={InlineBannerLayoutTypesEnum.MULTILINE}
      type={InlineBannerTypesEnum.INFO}
      onClose={onBannerClose}
      actions={[
        {
          id: 'view-in-analytics-action',
          title: 'View in Analytics',
          onClick: goToAnalytics,
          interactionId: 'view-in-analytics-action',
        },
      ]}
    >
      {
        <StyledDescriptionDiv>
          <Trans id="analytics-weekly-report-notice-up">
            Heads up! <b>Reply time up {Math.round(metricVariation * 100)}%</b> last week in this inbox.
          </Trans>
        </StyledDescriptionDiv>
      }
      {renderBars()}
    </StyledInfoBanner>
  );
};

/*
 * Hooks.
 */

function useInfoBannerContext(
  parameters: InboxesInboxParameters,
  analyticsInfoBannerDismissal: DismissedAnalyticsInfoBanner | undefined,
) {
  const shouldDismiss = shouldDismissCellsAnalyticsInfoBanner(analyticsInfoBannerDismissal);

  const getInbox = useMemo(() => makeGetInbox<void>(() => parameters.inboxId), [parameters.inboxId]);
  const inbox = useAppSelector(getInbox);

  if (shouldDismiss || !inbox) {
    return null;
  }

  const {cellsAnalytics, namespace} = inbox;
  const teamId = parseTeamNamespace(inbox.namespace);

  if (
    !namespace ||
    !cellsAnalytics ||
    cellsAnalytics.status !== AnalyticsJobStatusEnum.DONE ||
    !cellsAnalytics.metric ||
    !teamId
  ) {
    return null;
  }

  const {metric} = cellsAnalytics;

  if (
    !metric ||
    metric.metricType !== AnalyticsMetricTypesEnum.SCALAR ||
    metric.value === 0 ||
    !metric.previousValue
  ) {
    return null;
  }

  const metricVariation = (metric.value - metric.previousValue) / metric.previousValue;

  // only target 1/4 of the population, which at the time this is written is only the +25% population
  if (metricVariation < 0.25) {
    return null;
  }

  return {
    metric,
    namespace,
    inboxId: parameters.inboxId,
    metricId: cellsAnalytics.metricId,
    teamId,
    metricVariation,
  };
}
